'use client';

import { useState } from 'react';
import Link from 'next/link';
import { Sneaker } from '@/types/sneaker';
import { GuessResult, RoundRecord } from '@/types/game';
import { calculateScore } from '@/lib/game-logic';
import { useTranslation } from '@/lib/i18n/LanguageContext';
import { Landing } from '@/components/game/Landing';
import { GameHeader } from '@/components/game/GameHeader';
import { SneakerStage } from '@/components/game/SneakerStage';
import { PriceInput } from '@/components/game/PriceInput';
import { RoundResult } from '@/components/game/RoundResult';
import { FinalScreen } from '@/components/game/FinalScreen';

const TOTAL_ROUNDS = 10;
const MAX_SCORE = TOTAL_ROUNDS * 1000;

type Phase = 'landing' | 'playing' | 'over';

export default function Home() {
  const { t } = useTranslation();

  const [phase, setPhase] = useState<Phase>('landing');
  const [round, setRound] = useState(1);
  const [history, setHistory] = useState<RoundRecord[]>([]);

  const [sneaker, setSneaker] = useState<Sneaker | null>(null);
  const [seenIds, setSeenIds] = useState<string[]>([]);
  const [guessInput, setGuessInput] = useState('');
  const [result, setResult] = useState<GuessResult | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [hasError, setHasError] = useState(false);

  const score = history.reduce((sum, r) => sum + r.result.pointsEarned, 0);

  // Fetch a random sneaker, skipping the ones already played this game
  const loadSneaker = async (exclude: string[]) => {
    setIsLoading(true);
    setHasError(false);

    const request = async (ids: string[]): Promise<Sneaker> => {
      const query = ids.length > 0 ? `?exclude=${ids.join(',')}` : '';
      const response = await fetch(`/api/sneakers/random${query}`);
      if (!response.ok) throw new Error(response.statusText);
      return response.json();
    };

    let data: Sneaker | null = null;
    let usedExclude = exclude;
    try {
      data = await request(exclude);
    } catch {
      // Pool probably exhausted: forget the exclusions and retry once
      if (exclude.length > 0) {
        try {
          usedExclude = [];
          data = await request([]);
        } catch (retryError) {
          console.error('Error fetching sneaker:', retryError);
        }
      }
    }

    if (data) {
      setSneaker(data);
      setSeenIds([...usedExclude, data.id]);
      setGuessInput('');
      setResult(null);
    } else {
      setHasError(true);
    }
    setIsLoading(false);
  };

  const startGame = () => {
    setHistory([]);
    setRound(1);
    setSeenIds([]);
    setPhase('playing');
    loadSneaker([]);
  };

  const handleGuess = () => {
    const guessedPrice = Number(guessInput);
    if (!sneaker || !(guessedPrice > 0)) return;

    const actualPrice = sneaker.resalePrice || sneaker.retailPrice || 0;
    const guess = calculateScore(guessedPrice, actualPrice);
    setResult(guess);
    setHistory((prev) => [...prev, { sneaker, result: guess }]);
  };

  const handleNext = () => {
    if (round >= TOTAL_ROUNDS) {
      setPhase('over');
      return;
    }
    setRound((r) => r + 1);
    loadSneaker(seenIds);
  };

  if (phase === 'landing') {
    return <Landing onPlay={startGame} />;
  }

  if (phase === 'over') {
    return <FinalScreen history={history} maxScore={MAX_SCORE} onRestart={startGame} />;
  }

  const currentRecord = result ? history[history.length - 1] : null;

  return (
    // Fixed viewport height: the stage flexes to fill, the side panel sizes to its content
    <div className="flex h-dvh flex-col overflow-hidden">
      <GameHeader round={round} totalRounds={TOTAL_ROUNDS} score={score} history={history} />

      <main className="mx-auto flex min-h-0 w-full max-w-5xl flex-1 flex-col gap-3 px-4 py-3 lg:flex-row">
        {isLoading ? (
          <div className="card flex flex-1 flex-col items-center justify-center gap-4" aria-busy="true">
            <div className="h-9 w-9 animate-spin rounded-full border-4 border-line border-t-accent" />
            <p className="text-sm text-muted">{t('loading')}</p>
          </div>
        ) : hasError || !sneaker ? (
          <div className="card flex flex-1 flex-col items-center justify-center gap-4 p-6 text-center">
            <p className="text-muted">{t('noSneakerAvailable')}</p>
            <button onClick={() => loadSneaker(seenIds)} className="btn-primary px-6 py-3">
              {t('retry')}
            </button>
          </div>
        ) : (
          // Keyed on the sneaker so each round replays the entrance animation
          <>
            <SneakerStage key={sneaker.id} sneaker={sneaker} compact={!!currentRecord} />
            <aside className="flex min-h-0 shrink-0 flex-col overflow-y-auto lg:w-96">
              {currentRecord ? (
                <RoundResult record={currentRecord} isLastRound={round >= TOTAL_ROUNDS} onNext={handleNext} />
              ) : (
                <PriceInput value={guessInput} onChange={setGuessInput} onSubmit={handleGuess} />
              )}
            </aside>
          </>
        )}
      </main>

      <footer className="shrink-0 pb-2 text-center">
        <Link href="/legal" className="text-[11px] text-muted transition-colors hover:text-fg">
          {t('legalNotice')}
        </Link>
      </footer>
    </div>
  );
}
